import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { pb } from '../lib/pb';
import { errorMessage } from '../lib/errors';
import type { Role, UserRecord } from '../lib/types';
import { toast, useStore } from '../store/store';

function randomPassword(): string {
  const bytes = new Uint8Array(12);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789'[b % 56]).join('');
}

export function UsersAdmin() {
  const { t } = useTranslation();
  const users = useStore((s) => s.users);
  const me = useStore((s) => s.user);
  const list = Object.values(users).sort((a, b) => a.name.localeCompare(b.name));

  const setRole = async (u: UserRecord, role: Role) => {
    try {
      await pb.collection('users').update(u.id, { role });
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  };
  const resetPassword = async (u: UserRecord) => {
    const pw = randomPassword();
    if (!confirm(t('admin.users.confirmReset', { name: u.name, password: pw }))) return;
    try {
      await pb.collection('users').update(u.id, { password: pw, passwordConfirm: pw });
      void navigator.clipboard?.writeText(pw);
      toast(t('admin.users.passwordCopied'));
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  };

  return (
    <div className="admin-users">
      <table className="vertices small">
        <tbody>
          {list.map((u) => (
            <tr key={u.id}>
              <td>
                <div>{u.name || '—'}</div>
                <div className="muted">{u.email}</div>
              </td>
              <td>
                <select
                  value={u.role}
                  onChange={(e) => void setRole(u, e.target.value as Role)}
                  disabled={u.id === me?.id}
                >
                  {(['admin', 'editor', 'viewer'] as const).map((r) => (
                    <option key={r} value={r}>
                      {t(`roles.${r}`)}
                    </option>
                  ))}
                </select>
              </td>
              <td>
                <button className="link small" onClick={() => void resetPassword(u)}>
                  {t('admin.users.resetPassword')}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <NewUser />
    </div>
  );
}

function NewUser() {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('editor');
  const [lang, setLang] = useState<'sv' | 'en'>('sv');
  const [password, setPassword] = useState(randomPassword);
  const [busy, setBusy] = useState(false);

  const create = async () => {
    setBusy(true);
    try {
      await pb
        .collection('users')
        .create({ name, email, role, lang, password, passwordConfirm: password, emailVisibility: false });
      void navigator.clipboard?.writeText(`${email}\n${password}`);
      toast(t('admin.users.created'));
      setName('');
      setEmail('');
      setPassword(randomPassword());
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card">
      <div className="group-label">{t('admin.users.new')}</div>
      <label>
        {t('common.name')}
        <input value={name} onChange={(e) => setName(e.target.value)} />
      </label>
      <label>
        {t('login.email')}
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
      </label>
      <div className="row">
        <label>
          {t('admin.users.role')}
          <select value={role} onChange={(e) => setRole(e.target.value as Role)}>
            {(['admin', 'editor', 'viewer'] as const).map((r) => (
              <option key={r} value={r}>
                {t(`roles.${r}`)}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t('top.language')}
          <select value={lang} onChange={(e) => setLang(e.target.value as 'sv' | 'en')}>
            <option value="sv">Svenska</option>
            <option value="en">English</option>
          </select>
        </label>
      </div>
      <label>
        {t('login.password')}
        <input className="mono" value={password} onChange={(e) => setPassword(e.target.value)} />
      </label>
      <p className="muted small">{t('admin.users.passwordHint')}</p>
      <button
        className="primary"
        disabled={busy || !email || !name || password.length < 8}
        onClick={() => void create()}
      >
        {t('admin.users.create')}
      </button>
    </div>
  );
}
